import React, { useState, useContext, useRef, useEffect, useMemo, useCallback } from "react";
import emptyImage from "../images/empty.jpg";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common, MAX_MESSAGE_LENGTH, sendMessageErrorFor } from "../util/common";
import loader2 from "../images/loader_2.gif";
import { ChatContext, WebSocketContext } from "../context/contexts";
import { convertTo12HourFormat, getTimeAgo, formatBoldText } from "../util/helperFunctions";
import MessageComponent from "./message";
import MessageAttachments, {
  getAttachmentCategory,
  getAttachmentExtension,
  getAttachmentIconClass,
} from "./message-attachments";
import ReactionsIndicator from "./reusable/reactions-indicator";
import { groupReactions } from "../util/helperFunctions";
import { resizeFile } from "../hooks/resizeImage";
import ChatComposer from "./chat-composer/ChatComposer";
import "./view-thread-modal.css";

const NEAR_BOTTOM_PX = 120;

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// Splits raw message attachments into what MessageAttachments expects.
const buildAttachmentProps = (attachments = []) => {
  const resourcesUrl = process.env.REACT_APP_CHAT_RESOURCES_URL;
  const imageAttachments = attachments.filter((a) => a?.file_type?.includes("image"));
  const nonImageAttachments = attachments.filter((a) => !a?.file_type?.includes("image"));
  const lightboxImages = imageAttachments.map((a) => ({
    src: `${resourcesUrl}/${a.attachment}`,
    type: "image",
  }));
  return { imageAttachments, nonImageAttachments, lightboxImages };
};

function ViewThreadModal({ userId, handleImageClick, allowAiCommand = false }) {
  const Chat = useContext(ChatContext);
  const WS = useContext(WebSocketContext);
  const composerRef = useRef(null);
  const fileInputRef = useRef(null);

  const { user, content, createdAt, id, is_deleted, message_attachment, thread_replies, message_reactions } =
    Chat.threadViewDetails;

  const [selectedFile, setSelectedFile] = useState([]);
  const [filePreview, setFilePreview] = useState([]);

  const [isSendingMsg, setIsSendingMsg] = useState(false);
  // Read through a ref so the composer receives stable handlers.
  const composerHandlersRef = useRef({});
  const composerHandlers = useMemo(
    () => ({
      onSubmit: () => composerHandlersRef.current.onSubmit(),
      onPaste: (event) => composerHandlersRef.current.onPaste(event),
    }),
    [],
  );

  const [onlineUserData, setOnlineUserData] = useState(null);
  const [isPostExpanded, setIsPostExpanded] = useState(false);
  const [isPostTruncatable, setIsPostTruncatable] = useState(false);
  const messageBodyRef = useRef(null);

  useEffect(() => {
    const onlineUsers = Chat.onlineUsers;

    const onlineData = onlineUsers.find((onlineUser) => onlineUser.user_id === user?.id);
    setOnlineUserData(onlineData);
  }, [Chat.onlineUsers, user?.id]);

  useEffect(() => {
    setIsPostExpanded(false);
    setIsPostTruncatable(false);
  }, [id, content]);

  useEffect(() => {
    if (is_deleted || isPostExpanded) return;

    const messageBodyEl = messageBodyRef.current;
    if (!messageBodyEl) return;

    const frameId = requestAnimationFrame(() => {
      setIsPostTruncatable(messageBodyEl.scrollHeight > messageBodyEl.clientHeight + 2);
    });

    return () => cancelAnimationFrame(frameId);
  }, [content, is_deleted, isPostExpanded]);

  const allItems = useMemo(
    () => [...thread_replies].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [thread_replies],
  );

  // --- Scroll management: the body is the only scroller ---
  const scrollBodyRef = useRef(null);
  const lastReplyIdRef = useRef(null);
  const shouldStickToBottomRef = useRef(false);
  const [hasNewReplies, setHasNewReplies] = useState(false);

  const isNearBottom = () => {
    const el = scrollBodyRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const scrollToLatest = useCallback((behavior = "smooth") => {
    const el = scrollBodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    setHasNewReplies(false);
  }, []);

  const handleBodyScroll = () => {
    if (hasNewReplies && isNearBottom()) setHasNewReplies(false);
  };

  const latestReplyId = allItems.length ? allItems[allItems.length - 1].id : null;

  useEffect(() => {
    // Switching threads: start at the top and reset tracking.
    lastReplyIdRef.current = latestReplyId;
    setHasNewReplies(false);
    if (scrollBodyRef.current) scrollBodyRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (lastReplyIdRef.current === latestReplyId) return;
    lastReplyIdRef.current = latestReplyId;
    if (latestReplyId === null) return;

    if (shouldStickToBottomRef.current || isNearBottom()) {
      shouldStickToBottomRef.current = false;
      requestAnimationFrame(() => scrollToLatest());
    } else {
      setHasNewReplies(true);
    }
  }, [latestReplyId, scrollToLatest]);

  // --- Accessibility: Esc to close, focus composer on open, restore focus on close ---
  const closeModal = Chat.handleThreadViewVisible;
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        // Let the lightbox handle its own Esc.
        if (document.querySelector(".image-preview-modal")) return;
        closeModal();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === "function") previouslyFocused.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Mobile bottom sheet: follow the on-screen keyboard, swipe down to close ---
  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const dragRef = useRef({ active: false, startY: 0, dy: 0, startTime: 0 });

  useEffect(() => {
    const vv = window.visualViewport;
    const overlay = overlayRef.current;
    if (!vv || !overlay) return undefined;
    const sync = () => {
      overlay.style.setProperty("--thread-vv-height", `${vv.height}px`);
      overlay.style.setProperty("--thread-vv-top", `${vv.offsetTop}px`);
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, []);

  const isSheetViewport = () => window.matchMedia("(max-width: 41.25em)").matches;

  const handleSheetPointerDown = (event) => {
    if (!isSheetViewport() || event.target.closest("button")) return;
    dragRef.current = { active: true, startY: event.clientY, dy: 0, startTime: Date.now() };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (dialogRef.current) dialogRef.current.style.transition = "none";
  };

  const handleSheetPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    drag.dy = Math.max(0, event.clientY - drag.startY);
    if (dialogRef.current) dialogRef.current.style.transform = `translateY(${drag.dy}px)`;
  };

  const handleSheetPointerEnd = () => {
    const drag = dragRef.current;
    if (!drag.active) return;
    drag.active = false;
    const dialog = dialogRef.current;
    if (dialog) {
      dialog.style.transition = "";
      dialog.style.transform = "";
    }
    const velocity = drag.dy / Math.max(Date.now() - drag.startTime, 1);
    if (drag.dy > 110 || (drag.dy > 40 && velocity > 0.6)) closeModal();
  };

  // --- Composer attachments ---
  const selectedFileRef = useRef([]);
  selectedFileRef.current = selectedFile;
  useEffect(
    () => () => selectedFileRef.current.forEach((f) => URL.revokeObjectURL(f.filePreview)),
    [],
  );

  const handleFileChange = (event) => {
    const files = event.target.files;
    const maxFileSizeMB = process.env.REACT_APP_MAX_ATTACHMENT_SIZE_IN_MB;
    if (!files || files.length === 0) return;

    const additions = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size / 1024 / 1024 > maxFileSizeMB) {
        showSwal({
          title: "Something went wrong",
          icon: "error",
          text: `File ${file.name} exceeds 15MB and will not be uploaded.`,
          confirmButtonText: "OK",
        });
        continue;
      }
      const isDuplicate = selectedFileRef.current.some(
        (existing) => existing.file.name === file.name && existing.file.size === file.size,
      );
      if (isDuplicate) continue;
      additions.push({ filePreview: URL.createObjectURL(file), file, type: file.type });
    }

    if (additions.length === 0) return;
    setSelectedFile((prev) => [...prev, ...additions]);
    setFilePreview((prev) => [...prev, ...additions]);
  };

  const handleRemoveFile = (filePreviewUrl) => {
    URL.revokeObjectURL(filePreviewUrl);
    setFilePreview((prev) => prev.filter((file) => file.filePreview !== filePreviewUrl));
    setSelectedFile((prev) => prev.filter((file) => file.filePreview !== filePreviewUrl));
  };

  const buildProfileImageSrc = (userProfile) => {
    const profileImage =
      userProfile === "" || userProfile === null
        ? emptyImage
        : `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${userProfile}`;

    return profileImage;
  };

  const renderUserChatAttachment = (attachment) => {
    const { filePreview: previewUrl, file, type } = attachment;
    const isImage = type.includes("image");
    const isVideo = type.includes("video");
    const category = getAttachmentCategory(type, getAttachmentExtension(file.name));

    return (
      <li className={`thread-tray-item ${isImage || isVideo ? "thread-tray-item--media" : "thread-tray-item--file"}`} key={previewUrl}>
        {isImage ? (
          <button
            type='button'
            className='thread-tray-thumb'
            aria-label={`Preview ${file.name}`}
            onClick={() => handleImageClick([{ src: previewUrl, type: "image" }])}
          >
            <img src={previewUrl} alt='' />
          </button>
        ) : isVideo ? (
          <div className='thread-tray-thumb'>
            <video src={previewUrl} muted preload='metadata' />
            <i className='fa fa-play-circle thread-tray-play' aria-hidden='true'></i>
          </div>
        ) : (
          <div className='thread-tray-file'>
            <i className={`fa ${getAttachmentIconClass(category)}`} aria-hidden='true'></i>
            <span className='thread-tray-file-info'>
              <span className='thread-tray-file-name' title={file.name}>{file.name}</span>
              <span className='thread-tray-file-size'>{formatBytes(file.size)}</span>
            </span>
          </div>
        )}
        <button
          type='button'
          className='thread-tray-remove'
          aria-label={`Remove ${file.name}`}
          onClick={() => handleRemoveFile(previewUrl)}
        >
          <i className='fa fa-times' aria-hidden='true'></i>
        </button>
      </li>
    );
  };

  const handleMessageInputPaste = (event) => {
    const clipboardItems = event.clipboardData.items;
    const dataTransfer = new DataTransfer();

    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) dataTransfer.items.add(file);
      }
    }

    if (dataTransfer.files.length > 0) {
      handleFileChange({ target: { files: dataTransfer.files } });
    }
  };

  const handleRemoveFiles = () => {
    selectedFileRef.current.forEach((f) => URL.revokeObjectURL(f.filePreview));
    setSelectedFile([]);
    setFilePreview([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const totalAttachmentBytes = filePreview.reduce((sum, f) => sum + f.file.size, 0);

  const handleSendMessage = async () => {
    if (isSendingMsg) return;
    if (!Chat.storedConversationId) return showSwal(common.errors.INVALID_CONVERSATION);
    const { text: messageContent, mentions } =
      composerRef.current?.getMessage() || { text: "", mentions: [] };
    if (!messageContent.trim()) return showSwal(common.errors.EMPTY_MESSAGE);
    if (messageContent.length > MAX_MESSAGE_LENGTH) return showSwal(common.errors.MESSAGE_TOO_LONG);

    try {
      const formData = new FormData();
      if (selectedFile.length > 0) {
        for (const fileObj of selectedFile) {
          const resizedFile = await resizeFile(fileObj.file, 800, 800); // Set max dimensions
          formData.append("message_attachments", resizedFile, fileObj.file.name);
        }
      }
      formData.append(
        "data",
        JSON.stringify({
          message: messageContent,
          mention_data: mentions,
        })
      );
      setIsSendingMsg(true);

      const replyDetail = await ApiService.message.replyThreadMessage(formData, Chat.storedConversationId, id);
      const { success, message } = replyDetail;

      if (success) {
        setIsSendingMsg(false);
        composerRef.current?.clear();
        handleRemoveFiles();
        shouldStickToBottomRef.current = true;
        Chat.reFetchThreadDetails(id);
      } else {
        setIsSendingMsg(false);
        showSwal(sendMessageErrorFor(replyDetail));
      }
    } catch (err) {
      setIsSendingMsg(false);
      showSwal(common.errors.SEND_MESSAGE_ERROR);
    }
  };

  composerHandlersRef.current = {
    onSubmit: () => {
      if (Chat.isPermittedToChat) handleSendMessage();
    },
    onPaste: handleMessageInputPaste,
  };

  const handleDeleteMsg = async (msgId) => {
    try {
      const deleteMsg = await ApiService.message.deleteMessage(Chat.storedConversationId, msgId);

      const { success, conversation_action } = deleteMsg;
      if (!success) showSwal(common.errors.DELETE_MESSAGE_ERROR);
      WS.websocketInstance.sendMessage({ message: conversation_action, type: "user-actions-chat" });
      Chat.reFetchThreadDetails(id);
    } catch (err) {
      console.error(err);
    }
  };

  const groupedReaction = groupReactions(message_reactions);

  const userOnline = onlineUserData ? onlineUserData?.is_online : false;
  const lastLogin = userOnline ? "" : onlineUserData?.last_online ? getTimeAgo(onlineUserData?.last_online) : null;

  const rootAttachmentProps = buildAttachmentProps(message_attachment);

  return (
    <div
      className='modal-container view-thread-overlay'
      ref={overlayRef}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeModal();
      }}
    >
      <div
        className='modal-dialog view-thread-modal-dialog view-thread-modal-post'
        role='dialog'
        aria-modal='true'
        aria-labelledby='view-thread-title'
        ref={dialogRef}
      >
        <div
          className='view-thread-sheet-drag'
          onPointerDown={handleSheetPointerDown}
          onPointerMove={handleSheetPointerMove}
          onPointerUp={handleSheetPointerEnd}
          onPointerCancel={handleSheetPointerEnd}
        >
          <span className='view-thread-grabber' aria-hidden='true'></span>
          <header className='view-thread-modal-header'>
          <div className='view-thread-modal-heading'>
            <h3 id='view-thread-title'>Thread</h3>
            <small aria-live='polite'>
              {allItems.length} {allItems.length === 1 ? "reply" : "replies"}
            </small>
          </div>
          <button
            type='button'
            className='view-thread-close-btn'
            aria-label='Close thread'
            onClick={() => closeModal()}
          >
            <i className='fa fa-times' aria-hidden='true'></i>
          </button>
          </header>
        </div>

        <div className='modal-content view-thread-container'>
          <div className='view-thread-scroll-body' ref={scrollBodyRef} onScroll={handleBodyScroll}>
            <section className='view-thread-post' aria-label='Original message'>
              <div className='view-thread-post-header'>
                <div className='view-thread-avatar-wrap'>
                  <img src={buildProfileImageSrc(user?.profile_photo)} alt='profile' className='view-thread-avatar' />
                  <div className={`online-indicator-thread ${userOnline ? "online-user" : "offline-user"}`}>
                    <span className='tooltip'>
                      {`${userOnline ? "Online" : `${lastLogin !== null && lastLogin !== undefined ? lastLogin : "Offline"}`}`}
                    </span>
                  </div>
                </div>

                <div className='view-thread-author-block'>
                  <h4>{user?.display_name}</h4>
                  <small title={convertTo12HourFormat(createdAt)}>{getTimeAgo(createdAt)}</small>
                </div>
              </div>

              <div className='view-thread-post-content'>
                <div className='view-thread-user-chat'>
                  <span
                    ref={messageBodyRef}
                    className={`view-thread-message-body${!isPostExpanded ? " view-thread-message-body--clamped" : ""}`}
                  >
                    <MessageComponent message={content} is_deleted={is_deleted} />
                  </span>
                  {!is_deleted && isPostTruncatable ? (
                    <button
                      type='button'
                      className='view-thread-see-more-btn'
                      onClick={() => setIsPostExpanded((prev) => !prev)}
                    >
                      {isPostExpanded ? "See less" : "See more"}
                    </button>
                  ) : null}
                </div>

                {!is_deleted && (
                  <div className='view-thread-attachments view-thread-attachments--root'>
                    <MessageAttachments {...rootAttachmentProps} onImageClick={handleImageClick} align='receiver' />
                  </div>
                )}
              </div>

              <div className='view-thread-root-reactions view-chat-reactions view-chat-reactions-receiver'>
                {Object.keys(groupedReaction).map((reaction, index) => (
                  <ReactionsIndicator
                    reaction={`${reaction}`}
                    reaction_details={groupedReaction[reaction]}
                    key={index}
                    isThread={true}
                  />
                ))}
              </div>
            </section>

            <div className='view-thread-divider' role='separator'>
              <span>{allItems.length} {allItems.length === 1 ? "reply" : "replies"}</span>
            </div>

            <section className='view-thread-replies-section' aria-label='Thread replies'>
              {allItems.length === 0 ? (
                <p className='view-thread-empty'>No replies yet. Start the conversation below.</p>
              ) : null}
              <div className='view-thread-replies-container'>
                {allItems.map((thread_reply) => {
                  const onlineData = Chat.onlineUsers.find((onlineUser) => onlineUser.user_id === thread_reply?.user?.id);
                  const replyUserOnline = onlineData ? onlineData?.is_online : false;
                  const replyLastLogin = replyUserOnline ? "" : `${getTimeAgo(onlineData?.last_online)}`;
                  const canDelete = userId === thread_reply?.user?.id && !thread_reply?.is_deleted;

                  return (
                    <article className='view-thread-reply' key={thread_reply.id}>
                      <div className='view-thread-avatar-wrap'>
                        <img
                          src={buildProfileImageSrc(thread_reply?.user?.profile_photo)}
                          alt='profile'
                          className='view-thread-avatar view-thread-avatar-reply'
                        />
                        <div className={`online-indicator-thread ${replyUserOnline ? "online-user" : "offline-user"}`}>
                          <span className='tooltip'>{`${replyUserOnline ? "Online" : `${replyLastLogin}`}`}</span>
                        </div>
                      </div>

                      <div className='view-thread-reply-main'>
                        <div className='view-thread-reply-meta'>
                          <h4>{thread_reply?.user?.display_name}</h4>
                          <small title={convertTo12HourFormat(thread_reply?.createdAt)}>
                            {getTimeAgo(thread_reply?.createdAt)}
                          </small>
                        </div>

                        {thread_reply?.is_deleted ? (
                          <span className='deleted-chat'>DELETED CHAT</span>
                        ) : (
                          <span className='view-thread-comment-text'>{formatBoldText(thread_reply?.content)}</span>
                        )}

                        {!thread_reply?.is_deleted && thread_reply.message_attachment?.length > 0 ? (
                          <div className='view-thread-attachments view-thread-attachments--reply'>
                            <MessageAttachments
                              {...buildAttachmentProps(thread_reply.message_attachment)}
                              onImageClick={handleImageClick}
                              align='receiver'
                            />
                          </div>
                        ) : null}
                      </div>

                      {canDelete ? (
                        <button
                          type='button'
                          className='view-thread-reply-options'
                          aria-label='Delete thread reply'
                          onClick={() => handleDeleteMsg(thread_reply.id)}
                        >
                          <i className='fa fa-trash-o' aria-hidden='true'></i>
                        </button>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          </div>

          {hasNewReplies ? (
            <button type='button' className='view-thread-new-replies' onClick={() => scrollToLatest()}>
              <i className='fa fa-arrow-down' aria-hidden='true'></i> New replies
            </button>
          ) : null}

          <section className='view-thread-inputs' aria-label='Reply composer'>
            {filePreview.length > 0 ? (
              <div className='thread-tray'>
                <div className='thread-tray-header'>
                  <span>
                    {filePreview.length} {filePreview.length === 1 ? "file" : "files"} · {formatBytes(totalAttachmentBytes)}
                  </span>
                  <button type='button' className='thread-tray-clear' onClick={handleRemoveFiles}>
                    Clear all
                  </button>
                </div>
                <ul className='thread-tray-list'>{filePreview.map((_filePreview) => renderUserChatAttachment(_filePreview))}</ul>
              </div>
            ) : null}

            <div className='mention-composer'>
              <div className='view-thread-reply-inputs-container'>
                <ChatComposer
                  ref={composerRef}
                  variant='thread'
                  members={Chat.conversationMembersData}
                  onlineUsers={Chat.onlineUsers}
                  includeAiCommand={allowAiCommand}
                  placeholder={
                    !Chat.isPermittedToChat
                      ? "You are restricted from chatting in this conversation"
                      : "Write a reply..."
                  }
                  disabled={!Chat.isPermittedToChat}
                  submitOnEnter
                  listId='mention-picker-thread'
                  {...composerHandlers}
                />

                <div className='thread-reply-actions'>
                  <input
                    type='file'
                    id='thread-fileInput'
                    className='message__file-input'
                    onChange={(event) => {
                      handleFileChange(event);
                      event.target.value = "";
                    }}
                    multiple
                    ref={fileInputRef}
                  />

                  <button
                    type='button'
                    className='message__btn-file js-file-upload--btn msg-tool'
                    onClick={() => fileInputRef.current?.click()}
                    aria-label='Attach file to thread reply'
                    disabled={!Chat.isPermittedToChat}
                  >
                    <i className='fa fa-paperclip message__btn-file-icon'></i>
                  </button>

                  <button
                    type='button'
                    className='message__btn-send js-send-message--btn msg-tool'
                    data-receiver=''
                    data-cid=''
                    aria-label='Send thread reply'
                    disabled={!Chat.isPermittedToChat || isSendingMsg}
                    onClick={() => {
                      if (Chat.isPermittedToChat) {
                        handleSendMessage();
                      }
                    }}
                  >
                    {isSendingMsg ? (
                      <img src={loader2} className='loader-msg' alt='sending' />
                    ) : (
                      <i className='fa fa-paper-plane message__btn-send-icon'></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ViewThreadModal;
